import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Gear, Palette } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { SocialConnections } from "@/components/SocialConnections";
import { PabauBookings } from "@/components/PabauBookings";

export default function Settings() {
  const api = useApi();
  const [ws, setWs] = useState(null);
  const [autoHours, setAutoHours] = useState(48);
  const [brand, setBrand] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/workspace`).then((r) => {
      setWs(r.data);
      setAutoHours(r.data?.settings?.auto_approve_hours ?? 48);
      setBrand(r.data?.brand_guidelines || "");
    }).catch(() => toast.error("Failed to load settings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspace`, {
        brand_guidelines: brand,
        settings: { ...(ws?.settings || {}), auto_approve_hours: Number(autoHours) },
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-[12px] bg-[#5FA8B4] flex items-center justify-center text-white shrink-0">
          <Gear size={22} weight="fill" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#3C4048] tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Workspace Settings</h1>
          <p className="text-[#7A9090] text-sm">Manage this workspace's defaults, brand and social connections.</p>
        </div>
      </div>

      {/* Workspace defaults */}
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-6">
        <div>
          <Label className="text-[#3C4048] mb-2 block font-semibold">Workspace</Label>
          <p className="text-[#7A9090] text-sm">{ws?.name || "—"}</p>
        </div>
        <div>
          <Label htmlFor="auto-hours" className="text-[#3C4048] mb-2 block font-semibold">Auto-approve window (hours)</Label>
          <Input id="auto-hours" data-testid="auto-hours-input" type="number" value={autoHours} onChange={(e) => setAutoHours(e.target.value)} className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] max-w-[160px]" />
          <p className="text-[#7A9090] text-xs mt-1">"Silence is Approval" — posts auto-approve this many hours before their scheduled time.</p>
        </div>
        <div>
          <Label htmlFor="brand" className="text-[#3C4048] mb-2 block font-semibold">Brand guidelines</Label>
          <Textarea id="brand" data-testid="brand-input" value={brand} onChange={(e) => setBrand(e.target.value)} className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[100px]" placeholder="Voice, tone, do's and don'ts…" />
          <p className="text-[#7A9090] text-xs mt-1 flex items-center gap-1">
            <Palette size={13} /> Manage this workspace's palette, fonts and logo assets on the
            <Link to="/brand-assets" className="text-[#3A7A8C] font-semibold hover:underline" data-testid="link-brand-assets">Brand Assets</Link> page.
          </p>
        </div>
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px]">
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>

      {/* Social publishing — one unified panel: Facebook, Instagram, LinkedIn, TikTok, YouTube */}
      <SocialConnections />

      <PabauBookings />
    </div>
  );
}
